// import '../../corecomponents/PerfTest/whyDidYouRenderScript';
import React, { useEffect, useMemo } from 'react';
import { Context as ResponsiveContext } from 'react-responsive';
import {
  DEFAULT_REQUEST_CONTEXT,
  RequestContextProvider
} from '../../contexts/RequestContext';
import { BidBarrel } from '../../corecomponents/Ads/BidBarrel';
import StaticHead from '../../corecomponents/App/StaticHead';
import ErrorBoundary from '../../corecomponents/ErrorBoundary/ErrorBoundary';
import { GoogleAnalytics } from '../../corecomponents/Tracking/GoogleAnalytics';
import { Tealium } from '../../corecomponents/Tracking/Tealium';
import {
  getAppEarlyReturnComponent,
  getAppEarlyReturnGSSPErrorComponent
} from '../../functions/app/getAppEarlyReturnComponent';
import getResponsiveContextValue from '../../functions/utils/getResponsiveContextValue';
import { DeviceType } from '../../functions/utils/requestContextUtils';
import useCollapsedSkybox from '../../hooks/useCollapsedSkybox';
import useHasMounted from '../../hooks/useHasMounted';
import useIsAdmin from '../../hooks/useIsAdmin';
import usePageViewGuid from '../../hooks/usePageViewGuid';
import usePreventScrollRestoration from '../../hooks/usePreventScrollRestoration';
import useRouteUrlHistory from '../../hooks/useRouteUrlHistory';
import { IPageConfig, IProjectConfig } from '../../models/metaTypes';
import { AppWrapperProps } from '../../models/pageTypes';
import { nextReduxWrapper } from '../../redux/globalStore';
import CCPAScripts from './CCPAScripts';

const AppWrapper: React.FC<
  AppWrapperProps & {
    projectConfig: IProjectConfig;
    pageConfig?: IPageConfig;
  }
> = ({ Component, pageProps, projectConfig, children }) => {
  const {
    requestContext,
    gsspError,
    statusCode, // this is only mapped when next handles an error
    title, // this is only mapped when next handles an error
    pageConfig: gsspPageConfig,
    ...restOfPageProps
  } = pageProps;

  const normalizedRequestContext = Object.assign(
    {},
    DEFAULT_REQUEST_CONTEXT,
    requestContext
  );
  const { deviceType, appType } = normalizedRequestContext;

  // Error components cannot have gssp, so we attach pageConfig to the component instance
  const pageConfig = gsspPageConfig ?? (Component as any).pageConfig ?? {};

  const pageMetaData = pageConfig?.pageMetaData ?? {};

  const contextDataPoints = useMemo(
    () => ({
      ...pageProps?.tracking,
      ...projectConfig?.getContextDataPoints(pageProps)
    }),
    [projectConfig, pageProps]
  );

  const isAdmin = useIsAdmin();
  const hasMounted = useHasMounted();
  const pageViewGuid = usePageViewGuid();
  useCollapsedSkybox(
    deviceType === DeviceType.Mobile ||
      (pageConfig?.isSkyboxCollapsedByDefault ?? false)
  );
  usePreventScrollRestoration();

  const routeUrlHistory = useRouteUrlHistory();

  const pageType = pageConfig?.pageMetaData?.pageType ?? '';

  useEffect(() => {
    // @ts-ignore
    window.dataLayer = window.dataLayer || [];
    // @ts-ignore
    window.dataLayer.push({ event: 'optimize.activate' });
  }, [pageType]);

  const earlyReturnComponent =
    getAppEarlyReturnComponent(
      Component,
      pageConfig,
      projectConfig.projectMetaData,
      pageViewGuid,
      deviceType,
      isAdmin,
      statusCode,
      title,
      restOfPageProps,
      routeUrlHistory
    ) ||
    getAppEarlyReturnGSSPErrorComponent(
      pageConfig,
      projectConfig.projectMetaData,
      pageViewGuid,
      deviceType,
      isAdmin,
      gsspError
    );

  if (earlyReturnComponent) {
    return earlyReturnComponent;
  }

  const responsiveContextValue = getResponsiveContextValue(
    hasMounted,
    deviceType
  );

  return (
    <ErrorBoundary includeLayoutManager>
      <React.StrictMode>
        <StaticHead isAdmin={isAdmin} requestContext={requestContext} />
        <CCPAScripts />
        <BidBarrel
          projectConfig={projectConfig}
          pageConfig={pageConfig}
          deviceType={deviceType}
          appType={appType}
          pageViewGuid={pageViewGuid}
          contextDataPoints={contextDataPoints}
        />
        <Tealium
          projectMetaData={projectConfig?.projectMetaData}
          pageMetaData={pageMetaData}
          contextDataPoints={contextDataPoints}
          deviceType={deviceType}
          pageViewGuid={pageViewGuid}
        />
        <GoogleAnalytics trackingPath={pageMetaData?.tracking} />

        <ResponsiveContext.Provider value={responsiveContextValue}>
          <RequestContextProvider requestContext={requestContext}>
            {children}
          </RequestContextProvider>
        </ResponsiveContext.Provider>
      </React.StrictMode>
    </ErrorBoundary>
  );
};

export default nextReduxWrapper.withRedux(AppWrapper);
